import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { ColDef } from 'ag-grid-community';
import { TableConfig } from '../../../shared/components/table/table.interfaces';
import { ActionConfig } from '../../../shared/components/table/table-action-configs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DamageAnalyticsFormComponent } from '../damage-analytics/damage-analytics-form/damage-analytics-form.component';
import { ViewCommentsComponent } from '../cwr-offboardingCard/Offboarding-DetailsScreen/view-comments/view-comments.component';
import { AddCommentsComponent } from '../cwr-offboardingCard/Offboarding-DetailsScreen/add-comments/add-comments.component';
import { AssetManagementService } from '../asset-management-service';
import { DateUtils, getMonthDateRange } from '@app/shared/utils/date.utils';
import { DamageRecord } from '@app/shared/interfaces/asset-management.model';
import { ResourceProfilePageComponent } from '../cwr-offboardingCard/Offboarding-DetailsScreen/resource-profile-page/resource-profile-page.component';
import { ToastrService } from 'ngx-toastr';
import { ToasterService } from '@app/shared/services/toaster.service';
import { yearDateFormat } from '@app/shared/constants/dateFormat';

@Injectable({
  providedIn: 'root',
})
export class DamageAnalyticsTableService {
  // Opens the comments dialog for a given record ID

  private readonly dataSubject = new BehaviorSubject<DamageRecord[]>([]);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly selectedRowsSubject = new BehaviorSubject<DamageRecord[]>(
    [],
  );
  private readonly attachmentsSubject = new BehaviorSubject<File | null>(null);

  // Public observable for attachments
  public readonly attachments$ = this.attachmentsSubject.asObservable();

  // Public observables
  public readonly data$ = this.dataSubject.asObservable();
  public readonly loading$ = this.loadingSubject.asObservable();
  public readonly selectedRows$ = this.selectedRowsSubject.asObservable();

  public readonly tableConfig: TableConfig = {
    enableRowSelection: true,
    enableActions: true,
    enableSorting: true,
    enableFiltering: true,
    enableExport: true,
  };
  currentStartDate: string = '';
  currentEndDate: string = '';
  currentLocation: string = '';
  DamageAnalyticsData: DamageRecord[] = [];
  constructor(
    private dialog: MatDialog,
    private assetManagementService: AssetManagementService,
    private toasterService: ToastrService,
    private toaster: ToasterService,
  ) {
    this.initializeLocationFromSession();
    (window as any).openDamageAnalyticsComments = (
      employeeId: string,
      action?: string,
    ) => {
      this.openCommentsDialog(employeeId);
    };

    (window as any).openDamageResourceProfile = (id: string) => {
      const record = this.DamageAnalyticsData?.find(
        (r) => (r as any).employeeId === id.toString(),
      );
      if (record && record.employeeId) {
        this.openResourceProfileDialog(record.employeeId);
      } else {
        this.toasterService.error(
          'Employee record not found for Resource Profile',
        );
      }
    };
  }

  private initializeLocationFromSession(): void {
    this.currentLocation = this.assetManagementService.getUserLocationFromSession();
  }

  getCurrentLocation(): string {
    return history.state.selectedLocation;
  }

  loadInitialData(): void {
    const { startDate, endDate } = getMonthDateRange(
      history.state.selectedMonth,
      yearDateFormat.date,
    );
    this.currentStartDate = startDate;
    this.currentEndDate = endDate;
    const navigationLocation = history.state.selectedLocation;
    if (navigationLocation) {
      this.currentLocation = navigationLocation;
    }
    
    this.getDamageAnalyticsData(
      this.currentStartDate,
      this.currentEndDate,
      this.currentLocation,
    );
  }

  getDamageAnalyticsData(
    startDate: string,
    endDate: string,
    location?: string,
  ): void {
    const effectiveLocation = location || this.currentLocation;
    const effectiveStartDate =
      startDate != '' ? startDate : this.currentStartDate;
    const effectiveEndDate = endDate != '' ? endDate : this.currentEndDate;
    this.loadingSubject.next(true);
    this.assetManagementService
      .getDamageAnalyticsData(
        effectiveStartDate,
        effectiveEndDate,
        effectiveLocation,
      )
      .subscribe(
        (response) => {
          this.DamageAnalyticsData = response.data || response;
          this.dataSubject.next(this.DamageAnalyticsData);
          this.loadingSubject.next(false);
        },
        (error) => {
          this.loadingSubject.next(false);
        },
      );
  }

  updateDateRange(startDate: string, endDate: string): void {
    this.currentStartDate = startDate;
    this.currentEndDate = endDate;
  }

  updateLocationAndRefresh(location: string): void {
    this.currentLocation = location;
    this.getDamageAnalyticsData(
      this.currentStartDate,
      this.currentEndDate,
      location,
    );
  }

  openFormDialog() {
    const dialogRef = this.dialog.open(DamageAnalyticsFormComponent, {
      panelClass: 'custom-dialog-container',
      width: '632px',
      height: '876px',
      data: {},
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.getDamageAnalyticsData(
          this.currentStartDate,
          this.currentEndDate,
          this.currentLocation,
        );
      }
    });
  }

  openEditFormDialog(data: any) {
    const editData = { isEdit: true, ...data };
    const dialogRef = this.dialog.open(DamageAnalyticsFormComponent, {
      panelClass: 'custom-dialog-container',
      width: '632px',
      height: '876px',
      data: editData,
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.getDamageAnalyticsData(
          this.currentStartDate,
          this.currentEndDate,
          this.currentLocation,
        );
      }
    });
  }

  handleAction(action: string, data: DamageRecord): void {
    // Prevent any actions for cancelled records
    if (
      data.damageCostStatus === 'Cancelled' ||
      data.damageCostStatus === 'cancelled'
    ) {
      return;
    }

    switch (action) {
      case 'edit':
        this.openEditFormDialog(data);
        break;
      case 'delete':
        this.deleteRequest(data);
        break;
      case 'addRequest':
        this.openFormDialog();
        break;
      default:
        console.warn(`Unknown action: ${action}`);
    }
  }

  private deleteRequest(data: DamageRecord): void {
    const dialogRef = this.dialog.open(AddCommentsComponent, {
      data: { record: data, isCancelRow: true },
      width: '480px',
      panelClass: 'comments-dialog-panel',
    });
    const compInstance: any = dialogRef.componentInstance;
    if (compInstance?.save) {
      compInstance.save.subscribe((comment: string) => {
        this.assetManagementService
          .cancelDamageAnalyticsRecord(comment, data.employeeId || '', 'damage')
          .subscribe({
            next: (response) => {
              if (response) {
                dialogRef.close();
                this.toaster.showSuccess(
                  response?.message || 'Asset cancelled successfully.',
                  'Success',
                );
                this.getDamageAnalyticsData(
                  this.currentStartDate,
                  this.currentEndDate,
                  this.currentLocation,
                );
              }
            },
            error: (error) => {
              console.error('Error cancelling damage analytics record:', error);
            },
          });
      });
    }
    if (compInstance?.cancel) {
      compInstance.cancel.subscribe(() => dialogRef.close());
    }
  }

  setAttachment(file: File | null): void {
    this.attachmentsSubject.next(file);
  }

  refreshData(): void {
    this.getDamageAnalyticsData(
      this.currentStartDate,
      this.currentEndDate,
      this.currentLocation,
    );
  }

  openCommentsDialog(id: string | number): void {
    const idStr = id.toString();
    const record = this.dataSubject.value.find((r) => r.employeeId === idStr);
    if (!record) {
      return;
    }
    if (
      record.damageCostStatus === 'Cancelled' ||
      record.damageCostStatus === 'cancelled'
    ) {
      return;
    }

    const hasComments = !!record.comments && record.comments.length > 0;
    const dialogConfig: MatDialogConfig = {
      width: '720px',
      maxWidth: '80vw',
      panelClass: ['comments-dialog-panel', 'center-dialog'],
      autoFocus: false,
      disableClose: false,
    };
    if (hasComments) {
      this.openViewCommentsDialog(record, dialogConfig);
    } else {
      this.openAddCommentsDialog(record, dialogConfig);
    }
  }

  private openViewCommentsDialog(
    record: any,
    dialogConfig: MatDialogConfig,
  ): void {
    const dialogRef = this.dialog.open(ViewCommentsComponent, {
      ...dialogConfig,
      data: { record },
    });
    const compInstance: any = dialogRef.componentInstance;
    if (compInstance?.addComments) {
      compInstance.addComments.subscribe(() => {
        dialogRef.close();
        this.openAddCommentsDialog(record, dialogConfig);
      });
    }
  }
  getOffboardingById(employeeId: string): any {
    return this.dataSubject.value.find(
      (item) => item?.employeeId === employeeId.toString(),
    );
  }

  private openAddCommentsDialog(
    record: any,
    dialogConfig: MatDialogConfig,
  ): void {
    const dialogRef = this.dialog.open(AddCommentsComponent, {
      data: { record },
      ...dialogConfig,
    });
    const compInstance: any = dialogRef.componentInstance;
    if (compInstance?.save) {
      compInstance.save.subscribe((newComment: string) => {
        this.assetManagementService
          .updateDamageAnalyticsComments(newComment, record.employeeId, 'damage')
          .subscribe({
            next: (response) => {
              if (response) {
                this.getDamageAnalyticsData(
                  this.currentStartDate,
                  this.currentEndDate,
                  this.currentLocation,
                );
                this.toaster.showSuccess(
                  response?.message || 'Comments updated successfully.',
                  'Success',
                );
                dialogRef.close();
              }
            },
            error: (error) => {
              console.error('Error updating comments:', error);
            },
          });
      });
    }
    if (compInstance?.cancel) {
      compInstance.cancel.subscribe(() => dialogRef.close());
    }
  }

  openResourceProfileDialog(employeeId: string): void {
    const record = this.getOffboardingById(employeeId);
    if (!record) {
      return;
    }
    const dialogConfig: MatDialogConfig = {
      width: '1400px',
      height: '900px',
      panelClass: ['resource-profile-dialog-panel', 'center-dialog'],
      autoFocus: false,
      disableClose: false,
      data: { record, employeeId: record.employeeId },
    };
    this.dialog.open(ResourceProfilePageComponent, dialogConfig);
  }
}
